import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { dashboardService } from '@/services/dashboard';
import { useAuth } from '@/contexts/AuthContext';
import { useDeadlineRefresh } from '@/hooks/useDeadlineRefresh';
import { useMyStanding } from '@/hooks/useMyStanding';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { haptics } from '@/utils/haptics';
import { Summary } from '@/components/dashboard/Summary';
import { Picks } from '@/components/dashboard/Picks';
import { Fixtures } from '@/components/dashboard/Fixtures';
import { LeagueStandings } from '@/components/league/LeagueStandings';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function DashboardPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isEliminated } = useMyStanding();

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', user?.id],
    queryFn: () => dashboardService.getDashboard(user?.id || ''),
    enabled: !!user?.id,
    refetchInterval: 120000, // Refetch every 2 minutes to show live points during matches
  });

  // The dashboard query polls, but the picks control and the standings do not — they are
  // push-driven. Without this they keep showing no pick after an auto-pick until the page is
  // reloaded by hand, while the summary above them already counts it.
  useDeadlineRefresh(data?.currentGameweek?.deadline);

  // Pull-to-refresh functionality
  const { isPulling, pullDistance } = usePullToRefresh({
    onRefresh: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      await queryClient.invalidateQueries({ queryKey: ['picks'] });
      await queryClient.invalidateQueries({ queryKey: ['league-standings'] });
      haptics.success();
    },
    enabled: !isLoading,
  });

  if (isLoading || !user) {
    return (
      <div className="container mx-auto p-4" data-testid="dashboard-loading">
        <Card>
          <CardContent className="p-12">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
              <p className="text-lg text-muted-foreground">Loading your dashboard...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check for API errors
  if (error) {
    const axiosError = error as AxiosError;
    const isNetworkError = !axiosError.response;
    const isServerError = axiosError.response?.status && axiosError.response.status >= 500;
    const isUnauthorized =
      axiosError.response?.status === 401 || axiosError.response?.status === 403;

    if (isUnauthorized) {
      return (
        <div className="container mx-auto p-4">
          <Card>
            <CardHeader>
              <CardTitle>Approval Required</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Your participation in the current season is pending approval. You will have access
                once an admin approves your request.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (isNetworkError || isServerError) {
      return (
        <div className="container mx-auto p-4" data-testid="dashboard-error">
          <Card>
            <CardHeader>
              <CardTitle className="text-destructive">Unable to Connect to Server</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {isNetworkError
                  ? "We're having trouble connecting to the server. Please check your internet connection and try again."
                  : 'The server is experiencing issues. Please try again in a few moments.'}
              </p>
              <div className="bg-muted p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Troubleshooting Steps:</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Check your internet connection</li>
                  <li>Refresh the page</li>
                  <li>Clear your browser cache</li>
                  {user?.isAdmin && <li>Check the server status in the admin panel</li>}
                </ul>
              </div>
              <button
                data-testid="refresh-page-button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              >
                Refresh Page
              </button>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  // Check if there is an active season
  const hasActiveSeason = (data?.upcomingGameweeks?.length ?? 0) > 0 || !!data?.currentGameweek;

  if (!hasActiveSeason) {
    return (
      <div className="container mx-auto p-4" data-testid="dashboard-no-season">
        <Card>
          <CardHeader>
            <CardTitle>No Active Season</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              There is currently no active season or gameweeks scheduled.
            </p>
            {user?.isAdmin && (
              <div className="bg-blue-50 dark:bg-violet-500/10 p-4 rounded-lg border border-blue-200 dark:border-violet-800/60">
                <h3 className="font-semibold text-blue-800 dark:text-violet-300 mb-2">
                  Admin Action Required
                </h3>
                <p className="text-sm text-blue-700 dark:text-violet-300 mb-4">
                  As an admin, you can create a new season and generate gameweeks to get started.
                </p>
                <Link
                  to="/admin"
                  data-testid="admin-panel-link"
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                >
                  Go to Admin Panel
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    // From md up the dashboard is pinned to the viewport and the card row takes whatever is left
    // under the summary — and under the elimination banner when there is one. Cards scroll their
    // own contents rather than the page growing, so the row fits on screen whatever is above it
    // and whatever the league size. The subtraction is the header, which is a fixed 65px at
    // these widths (py-3 either side of a 40px logo, plus its border).
    //
    // Below md there is a single stacked column and the page scrolls normally, which is what a
    // phone wants — and leaves usePullToRefresh working against the window as it expects.
    <div
      className="container mx-auto p-3 sm:p-4 space-y-3 sm:space-y-4 md:h-[calc(100dvh-65px)] md:flex md:flex-col"
      data-testid="dashboard-content"
    >
      {/* Pull-to-Refresh Indicator */}
      {isPulling && (
        <div
          data-testid="pull-to-refresh-indicator"
          className="fixed top-0 left-0 right-0 flex items-center justify-center bg-primary/10 transition-all duration-200 z-50"
          style={{ height: `${pullDistance}px` }}
        >
          <div className="text-primary font-medium animate-pulse">
            {pullDistance >= 80 ? 'Release to refresh' : 'Pull to refresh'}
          </div>
        </div>
      )}

      {/* Summary Header. shrink-0 so the banner and tiles keep their natural height and the card
          row below absorbs the difference. */}
      <div className="md:shrink-0">
        <Summary />
      </div>

      {/* Three columns normally, two once a player is out. The picks control is not merely
          disabled for them, it is gone — they cannot pick again this season, so it would be a
          permanently dead card taking a third of the width. Dropping to two columns fills the
          space it leaves rather than parking an empty one on the end. */}
      <div
        className={`grid gap-4 grid-cols-1 md:grid-cols-2 md:flex-1 md:min-h-0 ${
          isEliminated ? 'lg:grid-cols-2' : 'lg:grid-cols-3'
        }`}
        data-testid="dashboard-grid"
      >
        {/* Picks is lifted out of the flow for the same reason as the standings below: its table
            runs to 38 gameweeks, so in the flow it would set the row height and the other two
            would stretch to it. With both positioned, Fixtures is the only card left deciding
            the row, and all three end up its height. */}
        {!isEliminated && (
          <div data-testid="dashboard-picks-column" className="md:relative">
            <div className="md:absolute md:inset-0">
              <Picks />
            </div>
          </div>
        )}

        {/* Fixtures is positioned like the other two now. With the row height coming from the
            viewport rather than from content, nothing needs to be left in the flow to set it —
            and leaving Fixtures in would let a long fixture list push the row past the screen
            again, which is the whole thing being fixed. */}
        <div data-testid="dashboard-fixtures-column" className="md:relative">
          <div className="md:absolute md:inset-0">
            <Fixtures />
          </div>
        </div>

        {/* The standings card is lifted out of the flow from md up so it cannot set the row's
            height. A grid row is as tall as its tallest item's content, so while the card was in
            the flow a 266-row table made the row 266 rows tall and every "fill the cell" rule
            resolved to exactly that — no constraint, no scrollbar. Absolutely positioned, it no
            longer contributes a height, so Picks and Fixtures decide the row and the card fills
            whatever they settle on. Below md there is a single column and nothing to match, so it
            stays in the flow and the card's own max-height caps it. */}
        <div data-testid="dashboard-standings-column" className="md:relative">
          <div className="md:absolute md:inset-0">
            <LeagueStandings compact />
          </div>
        </div>
      </div>
    </div>
  );
}
